import React, { useState, useEffect, useCallback, useRef } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../api/baseUrl";
import type { AddTreatmentOutletContext } from "../addTreatmentOutletContext";
import {
  emptyPlanForms,
  type PlanFormState,
} from "../treatmentDrafts/treatmentDraftStore";

const EMERGENCY_OPTIONS = [
  "Pain management with analgesics",
  "Abscess drainage of acute odontogenic infection",
  "Management of dentoalveolar trauma",
  "Initiation of RCT",
  "Other",
];

const PREVENTIVE_OPTIONS = [
  "Patient education and motivation on plaque control methods",
  "Oral hygiene instructions with Guided brushing",
  "Dietary analysis and intervention",
  "Preventive resin restorations and fissure sealants",
  "Professional topical fluoride application",
  "Other",
];

const STABILIZATION_OPTIONS = [
  "Full mouth scaling and polishing",
  "Correction of defective restorations of ____",
  "Initiation of RCT of ____",
  "Extraction of ____",
  "Restoration of ____",
  "Other",
];

const DEFINITIVE_OPTIONS = [
  "Completion of RCT and coronal restoration of ____",
  "Resin bonded bridge ____",
  "Implant supported single crown ____",
  "Implant supported bridge ____",
  "Implant supported denture ____",
  "Post and core build up followed by PFM crown ____",
  "Metal partial denture ____",
  "Other",
];

type CategoryKey = "Emergency" | "Preventive" | "Stabilization" | "Definitive";

interface PlanRow {
  id: string;
  optionLabel: string;
  note: string;
}

type FormState = PlanFormState;

function TreatmentPlan() {
  const { ID } = useParams<{ ID: string }>();
  const { drafts } = useOutletContext<AddTreatmentOutletContext>() ?? {};
  // Half-filled forms live in the shell's draft store so a tab switch keeps
  // them; Save All on the bottom bar adds them too.
  const planForms = drafts?.drafts.plan ?? emptyPlanForms();
  const savedAt = drafts?.savedAt ?? 0;
  const [emergencyRows, setEmergencyRows] = useState<PlanRow[]>([]);
  const [preventiveRows, setPreventiveRows] = useState<PlanRow[]>([]);
  const [stabilizationRows, setStabilizationRows] = useState<PlanRow[]>([]);
  const [definitiveRows, setDefinitiveRows] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [localForms, setLocalForms] = useState(emptyPlanForms);
  const formSetter = useCallback(
    (category: CategoryKey): React.Dispatch<React.SetStateAction<FormState>> =>
      (update) => {
        if (drafts) {
          drafts.setPlanForm(category, update);
          return;
        }
        setLocalForms((prev) => ({
          ...prev,
          [category]:
            typeof update === "function" ? update(prev[category]) : update,
        }));
      },
    [drafts]
  );
  const forms = drafts ? planForms : localForms;

  const fetchPlan = useCallback(() => {
    if (!ID) return;
    setLoading(true);
    baseUrl
      .get("treatment/find-treatment", { headers: { treatmentid: ID } })
      .then((res) => {
        const byCategory = (cat: string) =>
          (res.data?.data?.treatmentPlan || [])
            .filter((p: any) => p.category === cat)
            .map((item: any) => ({
              id: item._id,
              optionLabel: item.optionLabel || "",
              note: item.note || "",
            }));
        setEmergencyRows(byCategory("Emergency"));
        setPreventiveRows(byCategory("Preventive"));
        setStabilizationRows(byCategory("Stabilization"));
        setDefinitiveRows(byCategory("Definitive"));
      })
      .catch(() => toast.error("Failed to load treatment plan"))
      .finally(() => setLoading(false));
  }, [ID]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Save All from the bottom bar added rows while this tab was open.
  const lastSavedAtRef = useRef(savedAt);
  useEffect(() => {
    if (savedAt === lastSavedAtRef.current) return;
    lastSavedAtRef.current = savedAt;
    fetchPlan();
  }, [savedAt, fetchPlan]);

  const getRows = (category: CategoryKey): PlanRow[] => {
    switch (category) {
      case "Emergency": return emergencyRows;
      case "Preventive": return preventiveRows;
      case "Stabilization": return stabilizationRows;
      case "Definitive": return definitiveRows;
    }
  };

  const addToTable = async (category: CategoryKey, option: string, note: string, onSuccess?: () => void) => {
    if (!option.trim() || !ID) return;
    try {
      const { data } = await baseUrl.post(
        "treatment/add-treatment-plan-item",
        { category, optionLabel: option, note: note.trim() },
        { headers: { treatmentid: ID } }
      );
      const row: PlanRow = {
        id: data.data._id,
        optionLabel: data.data.optionLabel || option,
        note: data.data.note ?? note.trim(),
      };
      switch (category) {
        case "Emergency": setEmergencyRows((prev) => [...prev, row]); break;
        case "Preventive": setPreventiveRows((prev) => [...prev, row]); break;
        case "Stabilization": setStabilizationRows((prev) => [...prev, row]); break;
        case "Definitive": setDefinitiveRows((prev) => [...prev, row]); break;
      }
      onSuccess?.();
    } catch {
      toast.error("Failed to add item");
    }
  };

  const removeFromTable = async (category: CategoryKey, id: string) => {
    if (!ID) return;
    try {
      await baseUrl.post("treatment/remove-treatment-plan-item", {}, {
        headers: { treatmentid: ID, planitemid: id },
      });
      switch (category) {
        case "Emergency": setEmergencyRows((prev) => prev.filter((r) => r.id !== id)); break;
        case "Preventive": setPreventiveRows((prev) => prev.filter((r) => r.id !== id)); break;
        case "Stabilization": setStabilizationRows((prev) => prev.filter((r) => r.id !== id)); break;
        case "Definitive": setDefinitiveRows((prev) => prev.filter((r) => r.id !== id)); break;
      }
    } catch {
      toast.error("Failed to remove item");
    }
  };

  const renderTable = (category: CategoryKey, rows: PlanRow[]) => (
    <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      {rows.length > 0 ? (
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Treatment</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Note</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-24">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300">{row.optionLabel}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{row.note || "—"}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => removeFromTable(category, row.id)}
                    className="px-2.5 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 text-xs font-medium"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="py-6 text-center text-slate-400 dark:text-slate-500 bg-white dark:bg-slate-800">
          <p className="text-xs">No items yet. Add using the form above.</p>
        </div>
      )}
    </div>
  );

  const renderFormAndTable = (
    title: CategoryKey,
    formState: FormState,
    setFormState: React.Dispatch<React.SetStateAction<FormState>>,
    options: string[]
  ) => {
    const rows = getRows(title);
    return (
      <div key={title} className="bg-white dark:bg-slate-800 rounded-lg shadow border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
          <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">{title}</h4>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Treatment</label>
              <select
                value={formState.option}
                onChange={(e) => setFormState((p) => ({ ...p, option: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">Select...</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Note</label>
              <input
                type="text"
                value={formState.note}
                onChange={(e) => setFormState((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. teeth numbers or details..."
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                addToTable(title, formState.option, formState.note, () => setFormState({ option: "", note: "" }));
              }}
              disabled={!formState.option.trim()}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
          {renderTable(title, rows)}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {loading && (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && (
        <>
      {renderFormAndTable("Emergency", forms.Emergency, formSetter("Emergency"), EMERGENCY_OPTIONS)}
      {renderFormAndTable("Preventive", forms.Preventive, formSetter("Preventive"), PREVENTIVE_OPTIONS)}
      {renderFormAndTable("Stabilization", forms.Stabilization, formSetter("Stabilization"), STABILIZATION_OPTIONS)}
      {renderFormAndTable("Definitive", forms.Definitive, formSetter("Definitive"), DEFINITIVE_OPTIONS)}
        </>
      )}
    </div>
  );
}

export default TreatmentPlan;
